export type CatalogCategory = "Staking" | "Lending" | "Credit pools" | "Spot" | "Derivatives" | "Tokenized assets";
export type AssetIcon = string;
export type CatalogOffer = { provider: string; network: string; rate: string; rate_percent?: string; terms: string };

export type CatalogItem = {
  id: string;
  category: CatalogCategory;
  asset: string;
  symbol: string;
  icons: AssetIcon[];
  logo?: string;
  provider: string;
  network: string;
  rate: string;
  rate_percent?: string;
  lock: string;
  risks: string;
  source: string;
  enabled: boolean;
  offers?: CatalogOffer[];
};

export type CatalogResponse = {
  items: CatalogItem[];
  fetched_at: string;
  providers: Record<string, { available: boolean; error?: string }>;
};

const CATALOG_URL = "/api/v1/investment-gateway/catalog";
const CATALOG_CACHE_KEY = "alfa:pools:catalog:v9";
const CATALOG_CACHE_TTL = 15 * 60 * 1000;

const validCatalog = (body: any): body is CatalogResponse =>
  Boolean(body && Array.isArray(body.items) && typeof body.providers === "object" && body.fetched_at);

export function readPoolsCatalogCache(): CatalogResponse | null {
  try {
    const body = JSON.parse(sessionStorage.getItem(CATALOG_CACHE_KEY) || "null");
    if (!validCatalog(body) || Date.now() - Date.parse(body.fetched_at) > CATALOG_CACHE_TTL) return null;
    return body;
  } catch {
    return null;
  }
}

export async function fetchPoolsCatalog(signal?: AbortSignal): Promise<CatalogResponse> {
  const response = await fetch(CATALOG_URL, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal,
  });
  if (!response.ok) throw new Error(`Catalog request failed (${response.status})`);
  const body = await response.json() as CatalogResponse;
  if (!validCatalog(body)) {
    throw new Error("Catalog response is invalid");
  }
  try { sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(body)); } catch {}
  return body;
}

const numericRate = (offer: CatalogOffer) => {
  const value = Number(offer.rate_percent);
  return Number.isFinite(value) ? value : null;
};

export function groupCatalogItems(items: CatalogItem[]): CatalogItem[] {
  const groups = new Map<string, CatalogItem[]>();
  items.forEach((item) => {
    const key = `${item.category}:${item.symbol}`;
    groups.set(key, [...(groups.get(key) || []), item]);
  });
  return Array.from(groups.values()).map((group) => {
    const first = group[0];
    const offers = group.flatMap((item) => item.offers || [{
      provider: item.provider, network: item.network, rate: item.rate,
      rate_percent: item.rate_percent, terms: item.lock,
    }]);
    const comparable = offers.filter((offer) => numericRate(offer) !== null)
      .sort((a, b) => (numericRate(b) || 0) - (numericRate(a) || 0));
    if (group.length === 1) return { ...first, offers };
    const networks = Array.from(new Set(offers.map((offer) => offer.network)));
    const terms = Array.from(new Set(offers.map((offer) => offer.terms)));
    const orderedOffers = comparable.length === offers.length ? comparable : offers;
    const leadingOffer = orderedOffers[0];
    return {
      ...first,
      provider: comparable.length ? leadingOffer.provider : first.provider,
      network: comparable.length ? leadingOffer.network : networks.length > 1 ? "Multiple networks" : networks[0],
      rate: leadingOffer.rate,
      rate_percent: comparable.length ? leadingOffer.rate_percent : undefined,
      lock: comparable.length ? leadingOffer.terms : terms.length > 1 ? "Terms vary" : terms[0],
      offers: orderedOffers,
    };
  });
}
